// TYPE ELITE — Holiday & seasonal typography treatments.
// Each treatment is a full recipe: font direction, materials, patterns, finishes,
// palette hint, gradient hint, supporting elements, layout suggestion.
// Applied additively — nothing else in the recipe is overwritten unless the user chooses.

export const HOLIDAY_TREATMENTS = {
  'Halloween': [
    { id: 'hw-carved-pumpkin', label: 'Carved Pumpkin', fontStyle: 'Chunky',
      materialFamily: 'Natural / Sculpted', materialSubtype: 'Wood',
      finish: 'Weathered', paletteRef: 'Halloween Classic',
      gradientRef: 'gp-hw-classic', supporting: ['Pumpkins','Jack-O\'-Lanterns','Candles'],
      description: 'carved pumpkin lettering with glowing candlelight inside, orange skin and green stems' },
    { id: 'hw-spooky-gothic', label: 'Spooky Gothic', fontStyle: 'Blackletter',
      materialFamily: 'Metal', materialSubtype: 'Oxidized Metal', finish: 'Distressed',
      paletteRef: 'Halloween Classic', supporting: ['Ravens','Tombstones','Full Moon'],
      description: 'blackletter gothic serifs with dripping ink, weathered rust, moonlit atmosphere' },
    { id: 'hw-stitched-frank', label: 'Stitched Frankenstein', fontStyle: 'Chunky',
      materialFamily: 'Textile', materialSubtype: 'Stitched Appliqué', finish: 'Distressed',
      paletteRef: 'Halloween Classic', supporting: ['Bones','Skulls'],
      description: 'chunky letters with visible cross-stitches, torn seams and green-black patchwork' },
    { id: 'hw-bones', label: 'Bones & Skulls', fontStyle: 'Chunky',
      materialFamily: 'Natural / Sculpted', materialSubtype: 'Stone', finish: 'Weathered',
      paletteRef: 'Halloween Classic', supporting: ['Bones','Skulls','Spiders'],
      description: 'letters constructed from stacked bones and skulls with dusty cracks' },
    { id: 'hw-slime', label: 'Toxic Slime', fontStyle: 'Bubble',
      materialFamily: 'Liquid / Gel', materialSubtype: 'Slime', finish: 'Wet',
      paletteRef: 'Blacklight', gradientRef: 'gp-hw-slime',
      supporting: ['Splashes','Sparks'],
      description: 'drippy neon-green slime letters with glossy highlights and toxic bubbles' },
    { id: 'hw-blood-drip', label: 'Blood Drip Horror', fontStyle: 'Dripping',
      materialFamily: 'Liquid / Gel', materialSubtype: 'Blood', finish: 'Wet',
      paletteRef: 'Halloween Classic', gradientRef: 'gp-hw-bloodmoon',
      supporting: ['Splashes','Full Moon'],
      description: 'jagged serif letters with thick blood drips and wet crimson highlights' },
    { id: 'hw-vampire-chrome', label: 'Vampire Chrome', fontStyle: 'Blackletter',
      materialFamily: 'Metal', materialSubtype: 'Silver Chrome', finish: 'Mirrored',
      paletteRef: 'Horror', gradientRef: 'gp-chrome-mirror',
      supporting: ['Bats','Full Moon'],
      description: 'polished silver chrome gothic letters with red bleed highlights and bat silhouettes' },
  ],
  'Christmas / Winter': [
    { id: 'xm-candy-cane', label: 'Candy Cane', fontStyle: 'Bubble',
      materialFamily: 'Candy / Food', materialSubtype: 'Hard Candy', finish: 'Glossy',
      paletteRef: 'Traditional Christmas', gradientRef: 'gp-xm-goldred',
      supporting: ['Candy Canes','Ribbons','Snowflakes'],
      description: 'glossy red and white striped candy-cane letters with crisp peppermint sheen' },
    { id: 'xm-gingerbread', label: 'Gingerbread', fontStyle: 'Chunky',
      materialFamily: 'Candy / Food', materialSubtype: 'Frosting', finish: 'Matte',
      paletteRef: 'Traditional Christmas',
      supporting: ['Gingerbread','Snowflakes'],
      description: 'baked gingerbread letters with white icing trim, tiny candy accents and cozy crumb texture' },
    { id: 'xm-knit', label: 'Festive Knit', fontStyle: 'Bubble',
      materialFamily: 'Textile', materialSubtype: 'Cable Knit', finish: 'Fuzzy',
      paletteRef: 'Traditional Christmas',
      supporting: ['Winter Greenery','Snowflakes'],
      description: 'chunky knit stitch letters in Christmas red and green with visible cable pattern' },
    { id: 'xm-snowcapped', label: 'Snow-Capped', fontStyle: 'Chunky',
      materialFamily: 'Natural / Sculpted', materialSubtype: 'Snow', finish: 'Frosted',
      paletteRef: 'Winter Blues', gradientRef: 'gp-xm-frost',
      supporting: ['Snowflakes','Snow','Winter Greenery'],
      description: 'thick letters draped with fresh snow on top, powdered surface and ice-blue shadow' },
    { id: 'xm-icy-crystal', label: 'Icy Crystal', fontStyle: 'Blackletter',
      materialFamily: 'Glass / Gem', materialSubtype: 'Ice', finish: 'Frosted',
      paletteRef: 'Winter Blues', gradientRef: 'gp-season-winter',
      supporting: ['Snowflakes','Winter Greenery'],
      description: 'translucent frozen crystal letters with cold ice-blue rim light and drifting frost' },
    { id: 'xm-lights', label: 'Holiday Lights', fontStyle: 'Y2K',
      materialFamily: 'Synthetic / Fantasy', materialSubtype: 'Neon Tube', finish: 'Glowing',
      paletteRef: 'Traditional Christmas',
      supporting: ['Lights','Ornaments'],
      description: 'letters wrapped in glowing multicolor christmas string lights with warm bokeh glow' },
  ],
  'Valentine / Love': [
    { id: 'vd-romantic-script', label: 'Romantic Script', fontStyle: 'Formal Calligraphy',
      materialFamily: 'Metal', materialSubtype: 'Rose Gold Chrome', finish: 'Mirrored',
      paletteRef: 'Classic Love', gradientRef: 'gp-chrome-rose',
      supporting: ['Hearts','Roses','Romantic Flourishes'],
      description: 'elegant flowing calligraphy in mirror-polished rose gold with soft pink romantic glow' },
    { id: 'vd-hearts-inline', label: 'Hearts Inline', fontStyle: 'Bubble',
      materialFamily: 'Glass / Gem', materialSubtype: 'Crystal', finish: 'Glossy',
      paletteRef: 'Classic Love',
      supporting: ['Hearts','Pearls'],
      description: 'plump letters with tiny hearts nested inside the counters, glossy pink crystal surface' },
    { id: 'vd-roses-wrap', label: 'Roses Wrapped', fontStyle: 'Formal Calligraphy',
      materialFamily: 'Natural / Sculpted', materialSubtype: 'Flowers', finish: 'Satin',
      paletteRef: 'Moody Romance', gradientRef: 'gp-vd-romance',
      supporting: ['Roses','Romantic Flourishes'],
      description: 'letters wrapped with realistic red roses and dark green leaves, moody romantic lighting' },
    { id: 'vd-lace-pastel', label: 'Pastel Lace', fontStyle: 'Formal Calligraphy',
      materialFamily: 'Textile', materialSubtype: 'Lace', finish: 'Matte',
      paletteRef: 'Cupid Pastel', gradientRef: 'gp-vd-cupid',
      supporting: ['Hearts','Bows','Pearls'],
      description: 'delicate pastel pink lace lettering with fine floral patterns and soft romantic light' },
  ],
  'Easter / Spring': [
    { id: 'es-pastel-florals', label: 'Pastel Florals', fontStyle: 'Bubble',
      materialFamily: 'Natural / Sculpted', materialSubtype: 'Flowers', finish: 'Satin',
      paletteRef: 'Spring Pastel', gradientRef: 'gp-es-pastel',
      supporting: ['Tulips','Daisies','Butterflies'],
      description: 'rounded pastel letters covered in tiny springtime blossoms and butterflies' },
    { id: 'es-woven-basket', label: 'Woven Basket', fontStyle: 'Chunky',
      materialFamily: 'Textile', materialSubtype: 'Chunky Crochet', finish: 'Matte',
      paletteRef: 'Spring Pastel',
      supporting: ['Eggs','Baskets','Chicks'],
      description: 'letters woven like a wicker easter basket with pastel eggs tucked between fibers' },
    { id: 'es-eggshell', label: 'Painted Eggshells', fontStyle: 'Bubble',
      materialFamily: 'Glass / Gem', materialSubtype: 'Clear Resin', finish: 'Satin',
      paletteRef: 'Bunny Meadow', gradientRef: 'gp-es-meadow',
      supporting: ['Eggs','Bunnies','Grass'],
      description: 'letters shaped like decorated pastel easter eggs with painted patterns and tiny cracks' },
  ],
  'Fall / Thanksgiving': [
    { id: 'ft-harvest-leaves', label: 'Harvest Leaves', fontStyle: 'Chunky',
      materialFamily: 'Natural / Sculpted', materialSubtype: 'Foliage', finish: 'Matte',
      paletteRef: 'Autumn Warmth', gradientRef: 'gp-season-autumn',
      supporting: ['Leaves','Sunflowers','Pumpkins'],
      description: 'letters formed from layered orange, red and gold autumn leaves with acorns nested throughout' },
    { id: 'ft-copper-warm', label: 'Copper Warm Glow', fontStyle: 'Formal Calligraphy',
      materialFamily: 'Metal', materialSubtype: 'Copper', finish: 'Brushed',
      paletteRef: 'Desert Clay', gradientRef: 'gp-chrome-copper',
      supporting: ['Wheat','Warm Foliage','Harvest Baskets'],
      description: 'brushed copper calligraphy with warm harvest lighting and rustic golden atmosphere' },
    { id: 'ft-pumpkin-carved', label: 'Pumpkin Carved', fontStyle: 'Chunky',
      materialFamily: 'Natural / Sculpted', materialSubtype: 'Wood', finish: 'Weathered',
      paletteRef: 'Autumn Warmth',
      supporting: ['Pumpkins','Leaves','Apples'],
      description: 'letters carved from real pumpkins with visible ridges, seeds and glowing candle interior' },
  ],
  'Celebration': [
    { id: 'cb-birthday-confetti', label: 'Birthday Confetti', fontStyle: 'Bubble',
      materialFamily: 'Glass / Gem', materialSubtype: 'Crystal', finish: 'Glittered',
      paletteRef: 'Confetti Pop', gradientRef: 'gp-life-birthday',
      supporting: ['Balloons','Confetti','Cakes','Candles'],
      description: 'joyful puffy letters covered in colorful confetti, balloons and sparkle bokeh' },
    { id: 'cb-graduation-gold', label: 'Gold Graduation', fontStyle: 'Formal Calligraphy',
      materialFamily: 'Metal', materialSubtype: 'Gold Chrome', finish: 'Mirrored',
      paletteRef: 'Gold Graduation', gradientRef: 'gp-life-graduation',
      supporting: ['Graduation Caps','Diplomas'],
      description: 'polished gold chrome graduation letters with tassel, cap silhouette and diploma scroll' },
    { id: 'cb-wedding-blush', label: 'Wedding Blush', fontStyle: 'Formal Calligraphy',
      materialFamily: 'Metal', materialSubtype: 'Rose Gold Chrome', finish: 'Satin',
      paletteRef: 'Blush Wedding', gradientRef: 'gp-life-wedding',
      supporting: ['Rings','Romantic Flourishes','Roses'],
      description: 'delicate rose gold script wedding lettering with blush pink florals and soft light' },
    { id: 'cb-baby-boy', label: 'Baby Boy Cloud', fontStyle: 'Bubble',
      materialFamily: 'Textile', materialSubtype: 'Fleece', finish: 'Fuzzy',
      paletteRef: 'Baby Boy', gradientRef: 'gp-life-baby-boy',
      supporting: ['Baby Items','Bows'],
      description: 'soft baby blue cloudlike letters with fuzzy plush texture and gentle nursery light' },
    { id: 'cb-baby-girl', label: 'Baby Girl Pink', fontStyle: 'Bubble',
      materialFamily: 'Textile', materialSubtype: 'Fleece', finish: 'Fuzzy',
      paletteRef: 'Baby Girl', gradientRef: 'gp-life-baby-girl',
      supporting: ['Baby Items','Bows','Hearts'],
      description: 'pillowy pastel pink baby lettering with plush fuzzy fibers and bows' },
  ],
  'Patriotic / Cultural': [
    { id: 'pt-stars-stripes', label: 'Stars & Stripes', fontStyle: 'Varsity / Collegiate',
      materialFamily: 'Textile', materialSubtype: 'Embroidery', finish: 'Matte',
      paletteRef: 'Team Red & Blue',
      supporting: ['Flags','Stars','Fireworks'],
      description: 'bold varsity block letters filled with red white blue stars and stripes patch pattern' },
  ],
};

export const HOLIDAY_TREATMENT_KEYS = Object.keys(HOLIDAY_TREATMENTS);
